import { useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from '../api';
import { Loading, ErrorState, Empty } from './ui';

/**
 * Every prog. and cost. screen is about one project, so the project
 * is chosen once here rather than passed on every page.
 *
 * A single-project company never sees the picker: making someone
 * choose from a list of one is a step that only ever wastes a click.
 *
 * The choice is remembered per module, because someone working on
 * cost for one project is usually not working on progress for a
 * different one — but they might be, and forcing both to move
 * together would be worse than remembering them apart.
 */
type ProjectRow = { id: string; code: string; name: string; status: string };

export function ProjectScope({
  storageKey,
  emptyBody,
  children,
}: {
  storageKey: string;
  emptyBody: string;
  children: (projectId: string) => ReactNode;
}) {
  const [projects, setProjects] = useState<ProjectRow[] | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ projects: ProjectRow[] }>('/projects')
      .then((r) => {
        const active = r.projects.filter((p) => p.status === 'ACTIVE');
        const list = active.length > 0 ? active : r.projects;
        setProjects(list);
        const remembered = localStorage.getItem(storageKey);
        setProjectId(list.find((p) => p.id === remembered)?.id ?? list[0]?.id ?? null);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Could not load projects.'));
  }, [storageKey]);

  if (error) return <ErrorState message={error} />;
  if (!projects) return <Loading rows={4} />;
  if (projects.length === 0) return <Empty title="Belum ada proyek" body={emptyBody} />;
  if (!projectId) return <Loading rows={4} />;

  return (
    <>
      {projects.length > 1 && (
        <div className="filters">
          <select
            className="select"
            value={projectId}
            onChange={(e) => {
              setProjectId(e.target.value);
              localStorage.setItem(storageKey, e.target.value);
            }}
          >
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} — {p.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {children(projectId)}
    </>
  );
}
